import { Grid2, Paper, Tab, Tabs } from "@mui/material";
import type { ReactNode } from "react";
import {
	UTILITY_CONSUMPTION_UTILITY,
	type UtilityConsumptionMeter,
	type UtilityConsumptionUtility,
} from "../models/UtilityConsumption";
import { ConsumptionMeterTree } from "./ConsumptionMeterTree";

interface ConsumptionUtilityTabsProps {
	utility: UtilityConsumptionUtility;
	onUtilityChange: (utility: UtilityConsumptionUtility) => void;
}

export const ConsumptionUtilityTabs = ({
	utility,
	onUtilityChange,
}: ConsumptionUtilityTabsProps) => (
	<Tabs
		value={utility}
		onChange={(_, value: UtilityConsumptionUtility) => onUtilityChange(value)}
		aria-label="Utility type"
		variant="scrollable"
		scrollButtons={false}
		sx={{
			minHeight: 38,
			mb: "-1px",
			position: "relative",
			zIndex: 1,
			"& .MuiTabs-indicator": { display: "none" },
			"& .MuiTabs-flexContainer": { alignItems: "flex-end", gap: 0.5 },
			"& .MuiTab-root": {
				bgcolor: "#E7EAEE",
				border: 1,
				borderColor: "divider",
				borderTopLeftRadius: 8,
				borderTopRightRadius: 8,
				color: "#48525C",
				fontWeight: 500,
				minHeight: 38,
				px: 2.5,
				textTransform: "capitalize",
				"&:hover": { bgcolor: "#EFF1F4" },
			},
			"& .MuiTab-root.Mui-selected": {
				bgcolor: "background.paper",
				borderBottomColor: "background.paper",
				color: "text.primary",
				fontWeight: 600,
				minHeight: 42,
			},
		}}
	>
		{Object.values(UTILITY_CONSUMPTION_UTILITY).map((value) => (
			<Tab key={value} value={value} label={value} />
		))}
	</Tabs>
);

interface ConsumptionFilterBarProps {
	meters: UtilityConsumptionMeter[];
	isMeterLoading: boolean;
	selectedMeterIds: string[] | null;
	areaSelector?: ReactNode;
	children?: ReactNode;
	actions?: ReactNode;
	onMeterChange: (meterIds: string[] | null) => void;
}

export const ConsumptionFilterBar = ({
	meters,
	isMeterLoading,
	selectedMeterIds,
	areaSelector,
	children,
	actions,
	onMeterChange,
}: ConsumptionFilterBarProps) => (
	<Paper
		component="section"
		aria-label="Consumption filters"
		elevation={0}
		variant="outlined"
		sx={{
			borderTopLeftRadius: 0,
			borderTopRightRadius: 0,
			mb: 1,
			px: 1.5,
			py: 1,
		}}
	>
		<Grid2 container spacing={1} alignItems="center">
			{areaSelector && (
				<Grid2 size={{ xs: 12, sm: 6, lg: "auto" }} sx={{ minWidth: 150 }}>
					{areaSelector}
				</Grid2>
			)}
			<Grid2 size={{ xs: 12, sm: 6, lg: "auto" }} sx={{ minWidth: 190 }}>
				<ConsumptionMeterTree
					meters={meters}
					isLoading={isMeterLoading}
					selectedMeterIds={selectedMeterIds}
					onMeterChange={onMeterChange}
				/>
			</Grid2>
			{children}
			{actions && (
				<Grid2 size={{ xs: 12, lg: "auto" }} sx={{ ml: { lg: "auto" } }}>
					{actions}
				</Grid2>
			)}
		</Grid2>
	</Paper>
);
